import React, { useEffect, useState } from 'react';
import { userApi } from '../api/userApi';
import Avatar from './Avatar';
import Modal from './Modal';
import ServiceIcon from './ServiceIcon';

const DOC_LABELS = {
  id: 'Government ID',
  police: 'Police Check',
  insurance: 'Public Liability Insurance',
};

const DOC_TYPES = ['id', 'police', 'insurance'];

const DOC_STATUS_META = {
  approved: { label: 'Approved', className: 'bg-giggs-green/10 text-[#1a7d56]' },
  pending: { label: 'Pending', className: 'bg-amber-500/10 text-amber-700' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-600' },
  not_submitted: { label: 'Not submitted', className: 'bg-slate-100 text-giggs-muted' },
};

const OVERALL_STATUS_META = {
  approved: { label: 'Verified', className: 'bg-giggs-green/10 text-[#1a7d56]' },
  pending: { label: 'Pending', className: 'bg-amber-500/10 text-amber-700' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-600' },
};

const badgeClass = 'inline-flex rounded-full px-2.5 py-1 font-dm text-[11px] font-bold';

const Stars = ({ count }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((number) => (
      <svg key={number} width="13" height="13" fill={number <= count ? '#f59e0b' : '#DCE4DF'} viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </span>
);

const Section = ({ title, children }) => (
  <section className="border-t border-slate-100 px-5 py-3.5 first:border-t-0 sm:px-8">
    <h4 className="mb-3 mt-0 font-nunito text-[13px] font-extrabold uppercase tracking-[0.5px] text-giggs-ink">{title}</h4>
    {children}
  </section>
);

const Row = ({ label, value, fullWidth }) => (
  <div className={`flex min-w-0 flex-col gap-0.5 ${fullWidth ? 'md:col-span-2' : ''}`}>
    <span className="font-dm text-[11px] font-semibold uppercase tracking-[0.4px] text-giggs-muted">{label}</span>
    <span className="break-words font-dm text-[13px] text-[#2D3B35]">{value}</span>
  </div>
);

const providerRateLabel = (service) => {
  const min = Number(service?.hourlyRateMin);
  const max = Number(service?.hourlyRateMax);
  if (Number.isFinite(min) && Number.isFinite(max) && min === max) return `$${min}/hr`;
  if (Number.isFinite(min) && Number.isFinite(max)) return `$${min}–$${max}/hr`;
  if (Number.isFinite(min)) return `$${min}/hr`;
  if (Number.isFinite(max)) return `$${max}/hr`;
  return '';
};

const UserDetailsModal = ({ user, role = 'client', title, onClose }) => {
  const isProvider = role === 'provider';
  const displayName = user?.name || (isProvider ? 'Provider' : 'Client');

  const [reviews, setReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState({ count: 0, average: null });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');

  useEffect(() => {
    if (!user?._id) return undefined;
    let cancelled = false;
    setReviewsLoading(true);
    setReviewsError('');
    userApi
      .getReviews(user._id)
      .then((response) => {
        if (cancelled) return;
        setReviews(response.data || []);
        setReviewsStats(response.stats || { count: 0, average: null });
      })
      .catch((err) => {
        if (!cancelled) setReviewsError(err.message || 'Failed to load reviews.');
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?._id]);

  if (!user) return null;

  const fmtDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const location = user.location || {};
  const shortLocation = [location.suburb, location.state, location.postcode].filter(Boolean).join(' ') || '—';

  const offeredServices = isProvider && Array.isArray(user.services)
    ? user.services
        .map((item) => {
          const service = item?.service && typeof item.service === 'object' ? item.service : null;
          if (!service) return null;
          return {
            _id: service._id,
            name: service.name,
            slug: service.slug,
            icon: service.icon,
            hourlyRateMin: item.hourlyRateMin,
            hourlyRateMax: item.hourlyRateMax,
          };
        })
        .filter(Boolean)
    : [];

  const availability =
    isProvider && user.availability && typeof user.availability === 'object' && !Array.isArray(user.availability)
      ? user.availability
      : null;

  const dayOrder = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const weeklyAvailability = (availability?.weekly || [])
    .filter((item) => item.enabled)
    .sort((a, b) => (dayOrder[a.day] ?? 9) - (dayOrder[b.day] ?? 9));

  const todayStart = new Date(new Date().toDateString());
  const upcomingExceptions = (availability?.exceptions || [])
    .map((exception) => ({ ...exception, dateObj: new Date(exception.date) }))
    .filter((exception) => !Number.isNaN(exception.dateObj.getTime()) && exception.dateObj >= todayStart)
    .sort((a, b) => a.dateObj - b.dateObj);

  const fmtSlots = (slots) => (slots || []).length
    ? slots.map((slot) => `${slot.start}–${slot.end}`).join(', ')
    : 'Available';

  const modalTitle = title || (isProvider ? 'Provider Details' : 'Client Details');
  const sectionTitle = isProvider ? 'Provider Information' : 'Client Information';
  const overallMeta = OVERALL_STATUS_META[user.verificationStatus] || OVERALL_STATUS_META.pending;

  return (
    <Modal title={modalTitle} size="lg" onClose={onClose}>
      <div className="pt-2">
        <div className="mb-2 flex flex-col gap-3 border-b border-slate-100 px-5 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:pb-2">
          <div className="flex min-w-0 items-center gap-3.5">
            <Avatar
              user={user}
              size={56}
              gradient={isProvider ? 'linear-gradient(135deg, #C7E8D4, #AAF0C4)' : 'linear-gradient(135deg, #D3F9E0, #AAF0C4)'}
              fontSize={22}
            />
            <div className="min-w-0">
              <h3 className="m-0 truncate font-nunito text-base font-extrabold leading-[1.3] text-giggs-ink sm:text-[17px]">{displayName}</h3>
              <span className="font-dm text-xs font-medium text-giggs-muted">{shortLocation}</span>
            </div>
          </div>
          <div className="flex shrink-0 flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-1">
            <span className={`${badgeClass} ${isProvider ? 'bg-giggs-green/10 text-[#1a7d56]' : 'bg-amber-500/10 text-amber-700'}`}>
              {isProvider ? 'Provider' : 'Client'}
            </span>
            {reviewsStats.count > 0 && (
              <span className="flex items-center gap-1 font-dm text-[11px] text-giggs-muted">
                <Stars count={Math.round(reviewsStats.average || 0)} />
                <strong className="text-giggs-ink">{reviewsStats.average}</strong>
                ({reviewsStats.count})
              </span>
            )}
          </div>
        </div>

        <Section title={sectionTitle}>
          <div className="grid gap-x-4 gap-y-2.5 md:grid-cols-2">
            <Row label="Name" value={displayName} />
            <Row label="Email" value={user.email || '—'} />
            <Row label="Phone" value={user.phone || '—'} />
            {user.createdAt && <Row label="Member Since" value={fmtDate(user.createdAt)} />}
            {isProvider && user.abn && <Row label="ABN" value={user.abn} />}
            {user.status && <Row label="Account Status" value={user.status.charAt(0).toUpperCase() + user.status.slice(1)} />}
          </div>
        </Section>

        <Section title="Location">
          <div className="grid gap-x-4 gap-y-2.5 md:grid-cols-2">
            <Row label="Suburb" value={location.suburb || '—'} />
            <Row label="State" value={location.state || '—'} />
            <Row label="Postcode" value={location.postcode || '—'} />
            {location.address && <Row label="Address" value={location.address} fullWidth />}
          </div>
        </Section>

        {isProvider && (
          <Section title="Verification & Documents">
            <div className="mb-3 grid gap-x-4 gap-y-2.5 md:grid-cols-2">
              <Row label="Overall Status" value={<span className={`${badgeClass} ${overallMeta.className}`}>{overallMeta.label}</span>} />
              <Row
                label="Profile Picture"
                value={user.avatar ? (
                  <a href={user.avatar} target="_blank" rel="noopener noreferrer" className="font-semibold text-giggs-green underline underline-offset-2">View uploaded photo</a>
                ) : 'Not uploaded'}
              />
            </div>

            <div className="flex flex-col gap-2">
              {DOC_TYPES.map((type) => {
                const document = user.verificationDocuments?.[type] || {};
                const status = document.status || 'not_submitted';
                const meta = DOC_STATUS_META[status] || DOC_STATUS_META.not_submitted;
                return (
                  <div key={type} className="flex flex-col gap-3 rounded-[10px] border border-[#E8EDE9] bg-slate-50 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <span className="block font-nunito text-[13px] font-bold text-giggs-ink">{DOC_LABELS[type]}</span>
                      <span className="block break-words font-dm text-[11px] text-giggs-muted">
                        {document.url ? (
                          <>
                            {document.originalName || 'Document on file'}
                            {document.submittedAt && ` · Submitted ${fmtDate(document.submittedAt)}`}
                            {document.reviewedAt && ` · Reviewed ${fmtDate(document.reviewedAt)}`}
                          </>
                        ) : 'Not uploaded yet'}
                      </span>
                      {status === 'rejected' && document.rejectionReason && <span className="mt-1 block break-words font-dm text-[11px] leading-[1.4] text-red-500"><strong>Rejected:</strong> {document.rejectionReason}</span>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={`${badgeClass} ${meta.className}`}>{meta.label}</span>
                      {document.url && <a href={document.url} target="_blank" rel="noopener noreferrer" className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 font-dm text-[11px] font-semibold text-giggs-green no-underline transition-colors hover:border-giggs-green hover:bg-giggs-green/[0.06]">View</a>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {isProvider && user.bio && <Section title="About"><p className="m-0 whitespace-pre-line break-words font-dm text-[13px] leading-[1.65] text-giggs-text">{user.bio}</p></Section>}

        {isProvider && offeredServices.length > 0 && (
          <Section title="Offered Services">
            <div className="flex flex-wrap gap-1.5">
              {offeredServices.map((service) => {
                const rate = providerRateLabel(service);
                return (
                  <span key={service._id || service.slug} className="inline-flex items-center gap-1.5 rounded-full bg-giggs-green/[0.08] px-2.5 py-1 font-dm text-[11px] font-semibold text-giggs-green">
                    <ServiceIcon serviceId={service.icon || service.slug || ''} size={12} />
                    {service.name}
                    {rate && <span className="ml-1.5 font-semibold text-[#177552]">{rate}</span>}
                  </span>
                );
              })}
            </div>
          </Section>
        )}

        {availability && (weeklyAvailability.length > 0 || upcomingExceptions.length > 0) && (
          <Section title="Availability">
            {weeklyAvailability.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {weeklyAvailability.map((item) => <div key={item.day} className="flex items-baseline gap-2.5 font-dm text-[13px]"><span className="w-10 shrink-0 font-bold text-[#10271d]">{item.day}</span><span className="break-words text-[#41584d]">{fmtSlots(item.slots)}</span></div>)}
              </div>
            ) : <p className="m-0 font-dm text-[13px] text-giggs-muted">No recurring weekly hours set.</p>}

            {upcomingExceptions.length > 0 && (
              <div className="mt-3.5">
                <span className="font-dm text-[11px] font-semibold uppercase tracking-[0.4px] text-giggs-muted">Upcoming date overrides</span>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {upcomingExceptions.map((exception, index) => <span key={`${exception.date}-${index}`} className={`inline-flex items-center rounded-full px-2.5 py-1 font-dm text-[11px] font-semibold ${exception.available ? 'bg-amber-500/[0.12] text-amber-700' : 'bg-red-500/10 text-red-500'}`}>{fmtDate(exception.dateObj)} · {exception.available ? fmtSlots(exception.slots) : 'Day off'}</span>)}
                </div>
              </div>
            )}

            {availability.timezone && <p className="mb-0 mt-3 font-dm text-[11px] text-giggs-muted">Times in {availability.timezone}</p>}
          </Section>
        )}

        <Section title={`Reviews${reviewsStats.count ? ` (${reviewsStats.count})` : ''}`}>
          {reviewsLoading ? (
            <p className="m-0 font-dm text-[13px] text-giggs-muted">Loading reviews…</p>
          ) : reviewsError ? (
            <p className="m-0 font-dm text-[13px] text-red-500">{reviewsError}</p>
          ) : reviews.length === 0 ? (
            <p className="m-0 font-dm text-[13px] text-giggs-muted">No reviews yet.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {reviews.map((review) => (
                <article key={review.gigId} className="rounded-[10px] border border-amber-500/[0.18] bg-amber-500/[0.06] px-3.5 py-3">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2"><span className="break-words font-dm text-[11px] font-bold uppercase tracking-[0.4px] text-giggs-text">{review.fromName} · {review.service?.name || 'Service'}</span><Stars count={review.rating} /></div>
                  <div className="mt-0.5 font-dm text-[11px] text-giggs-muted">{review.gigTitle} · {fmtDate(review.date)}</div>
                  {review.comment && <p className="mb-0 mt-1.5 break-words font-dm text-[13px] italic leading-[1.55] text-giggs-text">“{review.comment}”</p>}
                </article>
              ))}
            </div>
          )}
        </Section>

        <div className="flex justify-end border-t border-slate-100 px-5 pb-6 pt-4 sm:px-8">
          <button type="button" className="cursor-pointer rounded-xl border-0 bg-slate-100 px-6 py-3 font-dm text-sm font-semibold text-giggs-text transition-colors hover:bg-slate-200" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
};

export default UserDetailsModal;
